import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../../../../../helpers/common';
import type { RootState } from '../../../../../redux/store';
import type { AiPromptId, AiPromptVersion } from '../contracts/ai-prompt.types';
import type { PresentationDocument } from '../contracts/ai-presentation.types';

export interface AiConversation {
  id: number;
  title: string | null;
  status: 'draft';
  createdAt: string;
}

export interface AiConversationSummary {
  id: number;
  title: string | null;
  status: 'active' | 'archived' | 'draft';
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  pinned: boolean;
}

export interface UpdateAiConversationRequest {
  conversationId: number;
  title?: string;
  pinned?: boolean;
}

export interface UpdateAiConversationResponse {
  id: number;
  title: string | null;
  pinned: boolean;
}

export interface CreateAiConversationRequest {
  title?: string;
}

export interface AiUserMessage {
  id: number;
  conversationId: number;
  role: 'user';
  content: string;
  createdAt: string;
}

export interface CreateAiUserMessageRequest {
  conversationId: number;
  content: string;
}

export interface AiConversationMessage {
  id: number;
  conversationId: number;
  role: 'user' | 'assistant';
  content: string | null;
  status: 'pending' | 'streaming' | 'completed' | 'failed' | 'cancelled';
  createdAt: string;
  presentation?: PresentationDocument;
}

export interface ListAiConversationsArgs {
  limit?: number;
}

export interface AiFeaturesResponse {
  features: {
    aiWorkspaceV2: boolean;
  };
}

export interface RefreshAiPresentationRequest {
  conversationId: number;
  messageId: number;
}

export interface RefreshAiPresentationResponse {
  presentation: PresentationDocument;
}

export interface ExportAiPresentationDatasetRequest {
  conversationId: number;
  messageId: number;
  nodeId: string;
  locale: string;
}

export interface ExportAiPresentationDatasetResponse {
  blob: Blob;
  filename: string;
}

export interface AiPromptCatalogItem {
  id: AiPromptId;
  version: AiPromptVersion;
  module: string;
  category: string;
  /** Locale-keyed prompt content from the DB catalog, e.g. { en: '...', he: '...' }. */
  defaultText: Record<string, string>;
  /** Stable icon class name suffix, e.g. 'menu-stock' → CSS class `icon-menu-stock`. */
  icon?: string;
}

export const aiConversationsApi = createApi({
  reducerPath: 'aiConversationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      return headers;
    },
  }),
  tagTypes: ['AiConversation'],
  endpoints: (build) => ({
    listAiConversations: build.query<
      AiConversationSummary[],
      ListAiConversationsArgs
    >({
      query: (args) => ({
        url: `/ai/conversations?limit=${args.limit ?? 5}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        data: { list: AiConversationSummary[] };
      }) => response.data.list,
      providesTags: ['AiConversation'],
    }),
    listAiPromptProfiles: build.query<AiPromptCatalogItem[], { module?: string } | void>({
      query: (args) => ({
        url: '/ai/prompt-library',
        method: 'GET',
        params: args && (args as { module?: string }).module
          ? { module: (args as { module?: string }).module }
          : undefined,
      }),
      transformResponse: (response: {
        data: { list: AiPromptCatalogItem[] };
      }) => response.data.list,
    }),
    getAiFeatures: build.query<AiFeaturesResponse['features'], void>({
      query: () => ({ url: '/ai/features', method: 'GET' }),
      transformResponse: (response: { data: AiFeaturesResponse }) =>
        response.data.features,
    }),
    createAiConversation: build.mutation<
      AiConversation,
      CreateAiConversationRequest | void
    >({
      query: (body) => ({
        url: '/ai/conversations',
        method: 'POST',
        body: body ?? {},
      }),
      transformResponse: (response: { data: AiConversation }) => response.data,
      invalidatesTags: ['AiConversation'],
    }),
    listAiConversationMessages: build.query<AiConversationMessage[], number>({
      query: (conversationId) => ({
        url: `/ai/conversations/${conversationId}/messages`,
        method: 'GET',
      }),
      transformResponse: (response: {
        data: { list: AiConversationMessage[] };
      }) => response.data.list,
      providesTags: (_result, _error, conversationId) => [
        { type: 'AiConversation', id: conversationId },
      ],
    }),
    createAiUserMessage: build.mutation<
      AiUserMessage,
      CreateAiUserMessageRequest
    >({
      query: ({ conversationId, content }) => ({
        url: `/ai/conversations/${conversationId}/messages`,
        method: 'POST',
        body: { content },
      }),
      transformResponse: (response: { data: AiUserMessage }) => response.data,
      invalidatesTags: ['AiConversation'],
    }),
    refreshAiPresentation: build.mutation<
      RefreshAiPresentationResponse,
      RefreshAiPresentationRequest
    >({
      query: ({ conversationId, messageId }) => ({
        url: `/ai/conversations/${conversationId}/messages/${messageId}/presentation/refresh`,
        method: 'POST',
      }),
      transformResponse: (response: { data: RefreshAiPresentationResponse }) =>
        response.data,
    }),
    exportAiPresentationDataset: build.mutation<
      ExportAiPresentationDatasetResponse,
      ExportAiPresentationDatasetRequest
    >({
      query: ({ conversationId, messageId, nodeId, locale }) => ({
        url: `/ai/conversations/${conversationId}/messages/${messageId}/presentation/nodes/${encodeURIComponent(nodeId)}/export.xlsx`,
        method: 'GET',
        params: { locale },
        responseHandler: async (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta) => {
        const contentDisposition = meta?.response?.headers.get('Content-Disposition') ?? '';
        const encodedName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
        const quotedName = contentDisposition.match(/filename="([^"]+)"/i)?.[1];

        return {
          blob,
          filename: encodedName
            ? decodeURIComponent(encodedName)
            : quotedName || 'dataset-export.xlsx',
        };
      },
    }),
    updateAiConversation: build.mutation<
      UpdateAiConversationResponse,
      UpdateAiConversationRequest
    >({
      query: ({ conversationId, ...body }) => ({
        url: `/ai/conversations/${conversationId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: { data: UpdateAiConversationResponse }) =>
        response.data,
      // Optimistic update: flip pinned/title in the cached list immediately
      // so the rail feels instant, then invalidate to sync from server.
      onQueryStarted: async ({ conversationId, pinned, title }, { dispatch, queryFulfilled, getState }) => {
        const state = getState() as any;
        const patches: ReturnType<typeof dispatch>[] = [];
        // Update all cached listAiConversations results
        for (const [key, entry] of Object.entries(state.aiConversationsApi.queries)) {
          if (key.startsWith('listAiConversations') && (entry as any)?.data) {
            const patch = dispatch(
              aiConversationsApi.util.updateQueryData(
                'listAiConversations',
                (entry as any).originalArgs,
                (draft: AiConversationSummary[]) => {
                  const item = draft.find((c) => c.id === conversationId);
                  if (item) {
                    if (pinned !== undefined) item.pinned = pinned;
                    if (title !== undefined) item.title = title;
                  }
                },
              ),
            );
            patches.push(patch);
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p: any) => p.undo?.());
        }
      },
      invalidatesTags: ['AiConversation'],
    }),
  }),
});

export const {
  useListAiConversationsQuery,
  useListAiPromptProfilesQuery,
  useLazyListAiConversationMessagesQuery,
  useCreateAiConversationMutation,
  useCreateAiUserMessageMutation,
  useRefreshAiPresentationMutation,
  useExportAiPresentationDatasetMutation,
  useUpdateAiConversationMutation,
  useGetAiFeaturesQuery,
} = aiConversationsApi;
